import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card, SectionTitle, Pill, DEPT_LABELS, Loader } from "@/components/common";
import { GaugeCircle, AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";

export default function Accountability() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/screens/accountability");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader label="Aggregating department metrics" />;

  const maxScore = Math.max(1, ...data.rows.map((r) => r.bottleneck_score));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Department Accountability</h1>
        <p className="text-muted-foreground text-sm mt-1">Who is entering late or inaccurate data — and where the bottleneck is.</p>
      </div>

      {data.bottleneck_dept && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-5 border-critical/40 bg-critical/5" data-testid="bottleneck-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-critical/15 border border-critical/30 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-critical" size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-critical/80">Current bottleneck</div>
                <div className="font-heading text-2xl">{DEPT_LABELS[data.bottleneck_dept]}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  This department has the highest bottleneck score. Expedite here first — it's holding back the most work.
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      <section>
        <SectionTitle icon={GaugeCircle}>Department scorecard</SectionTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.rows.map((r) => {
            const isBottleneck = r.dept === data.bottleneck_dept;
            return (
              <Card key={r.dept} className={`p-5 ${isBottleneck ? "border-critical/40" : ""}`} data-testid={`dept-card-${r.dept}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-heading text-lg">{DEPT_LABELS[r.dept]}</span>
                  {isBottleneck && <Pill tone="critical">Bottleneck</Pill>}
                </div>

                <div className="space-y-3">
                  <Metric label="Late-update rate" value={`${r.late_update_rate}%`} tone={r.late_update_rate > 0 ? "critical" : "success"} />
                  <Metric
                    label="Hours variance"
                    value={`${r.hours_variance > 0 ? "+" : ""}${r.hours_variance}%`}
                    tone={r.hours_variance > 0 ? "warning" : "success"}
                    icon={r.hours_variance > 0 ? TrendingUp : TrendingDown}
                  />
                  <Metric label="Overdue tasks" value={r.overdue_count} tone={r.overdue_count > 0 ? "critical" : "muted"} />
                  <Metric label="Total tasks" value={r.total_tasks} tone="muted" />
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                    <span>Bottleneck score</span>
                    <span className="font-mono-num text-foreground">{r.bottleneck_score}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(r.bottleneck_score / maxScore) * 100}%`, backgroundColor: isBottleneck ? "hsl(var(--critical))" : "hsl(var(--gold))" }} />
                  </div>
                </div>

                {r.overdue_reasons && Object.keys(r.overdue_reasons).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border" data-testid={`overdue-reasons-${r.dept}`}>
                    <div className="text-xs text-muted-foreground mb-2">Overdue reasons</div>
                    <div className="space-y-1.5">
                      {Object.entries(r.overdue_reasons)
                        .sort((a, b) => b[1] - a[1])
                        .map(([reason, count]) => (
                          <div key={reason} className="flex items-center justify-between text-xs">
                            <span className="text-foreground/80 truncate pr-2">{reason}</span>
                            <span className="font-mono-num text-critical shrink-0">×{count}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Aggregated over each department's active tasks plus those completed in the last 30 days. bottleneckScore = lateUpdateRate + (overdueCount × 20).
        </p>
      </section>
    </div>
  );
}

const TONE = { critical: "text-critical", warning: "text-warning", success: "text-success", muted: "text-foreground" };

function Metric({ label, value, tone = "muted", icon: Icon }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-mono-num text-sm font-semibold flex items-center gap-1 ${TONE[tone]}`}>
        {Icon && <Icon size={13} />}{value}
      </span>
    </div>
  );
}
